import { create } from "zustand";
import type { MatchResult } from "@/services/pdf-matcher";

export interface FileMatch {
  file: File;
  extractedTitle: string | null;
  matches: MatchResult[];
  bestMatch: MatchResult | null;
  selectedPaperId: string | null;
  createAsNew?: boolean;
  status: "pending" | "uploading" | "done" | "error";
  errorMessage?: string;
}

export type UploadStage =
  | "idle"
  | "matching"
  | "review"
  | "uploading"
  | "done";

interface UploadProgress {
  completed: number;
  succeeded: number;
  failed: number;
  total: number;
}

interface UploadState {
  files: FileMatch[];
  stage: UploadStage;
  uploadProgress: UploadProgress;

  setFiles: (files: FileMatch[]) => void;
  setStage: (stage: UploadStage) => void;
  updateFileMatch: (
    index: number,
    update: Partial<FileMatch>,
  ) => void;
  updateProgress: (update: Partial<UploadProgress>) => void;
  markFileStatus: (
    index: number,
    status: FileMatch["status"],
    errorMessage?: string,
  ) => void;
  reset: () => void;
}

const initialProgress: UploadProgress = {
  completed: 0,
  succeeded: 0,
  failed: 0,
  total: 0,
};

export const useUploadStore = create<UploadState>((set) => ({
  files: [],
  stage: "idle",
  uploadProgress: { ...initialProgress },

  setFiles: (files) => set({ files }),

  setStage: (stage) => set({ stage }),

  updateFileMatch: (index, update) =>
    set((state) => {
      const files = [...state.files];
      files[index] = { ...files[index], ...update };
      return { files };
    }),

  updateProgress: (update) =>
    set((state) => ({
      uploadProgress: { ...state.uploadProgress, ...update },
    })),

  markFileStatus: (index, status, errorMessage) =>
    set((state) => {
      const files = [...state.files];
      files[index] = { ...files[index], status, errorMessage };

      const completed = files.filter(
        (f) => f.status === "done" || f.status === "error",
      ).length;
      const succeeded = files.filter((f) => f.status === "done").length;
      const failed = files.filter((f) => f.status === "error").length;

      return {
        files,
        uploadProgress: {
          completed,
          succeeded,
          failed,
          total: state.uploadProgress.total,
        },
      };
    }),

  reset: () =>
    set({
      files: [],
      stage: "idle",
      uploadProgress: { ...initialProgress },
    }),
}));
